import {
  type JSX,
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore
} from 'react'

import {
  DARK_CLASS,
  DARK_QUERY,
  DEFAULT_PREFERENCE,
  type Theme,
  type ThemePreference,
  nextPreference,
  resolveTheme
} from '../lib/theme.ts'
import { getPreferences, updatePreferences } from '../lib/preference-store.ts'
import { usePreferences } from '../hooks/use-preferences.ts'

/** What `useTheme` hands a consumer. */
export interface ThemeContextValue {
  /** What is painted right now, with `system` already resolved. */
  theme: Theme
  /** What the reader holds, which may be `system`. */
  preference: ThemePreference
  /** Flip the painted theme, and hold the result. */
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

/**
 * Whether the OS asks for dark right now.
 *
 * `matchMedia` is guarded because it is absent in jsdom unless a test stubs
 * it, and a provider that throws there would take every component test with
 * it. Absent reads as "no preference", which resolves to light.
 *
 * @returns True when the OS asks for dark.
 */
function systemPrefersDark(): boolean {
  return globalThis.matchMedia?.(DARK_QUERY).matches ?? false
}

/**
 * Re-render when the OS setting changes.
 *
 * Defined outside the component because `useSyncExternalStore` re-subscribes
 * whenever this identity changes, and a new closure per render would tear the
 * listener down and build it up on every paint.
 *
 * @param onChange - Called by React when it must re-read the setting.
 * @returns The unsubscribe function.
 */
function subscribeToSystem(onChange: () => void): () => void {
  const query = globalThis.matchMedia?.(DARK_QUERY)
  query?.addEventListener('change', onChange)

  return () => query?.removeEventListener('change', onChange)
}

/**
 * Read the theme, and flip it.
 *
 * @returns The theme context.
 * @throws When called outside a `ThemeProvider`.
 */
export function useTheme(): ThemeContextValue {
  const value = use(ThemeContext)
  if (!value) {
    throw new Error('useTheme must be called inside a ThemeProvider')
  }

  return value
}

/**
 * ThemeProvider — holds the theme, and paints it on the document element.
 *
 * It wraps the whole app rather than the console, so the login screen is
 * themed too. Nothing below it needs to know where the choice came from.
 *
 * The class it sets is the one `src/index.css` points Tailwind's `dark:`
 * variant at. `index.html` sets the same class before first paint, so the
 * effect here re-asserts a class that is usually already correct; that is what
 * keeps a dark-preferring reader from seeing a white flash.
 *
 * @param props - Provider props.
 * @param props.children - The app.
 * @returns The provider.
 */
export function ThemeProvider({
  children
}: {
  children: ReactNode
}): JSX.Element {
  // The settings store, not local state: a signed-in reader's theme follows
  // them to another machine, and the server's answer must repaint this one.
  const preference = usePreferences().theme ?? DEFAULT_PREFERENCE

  // The OS setting is external state that changes without React, which is
  // exactly what useSyncExternalStore is for. The third argument is the
  // server snapshot; this app never renders on a server, but React demands
  // one and light is the safe answer.
  const prefersDark = useSyncExternalStore(
    subscribeToSystem,
    systemPrefersDark,
    () => false
  )

  const theme = resolveTheme(preference, prefersDark)

  useEffect(() => {
    document.documentElement.classList.toggle(DARK_CLASS, theme === 'dark')
  }, [theme])

  const toggle = useCallback(() => {
    const held = getPreferences().theme ?? DEFAULT_PREFERENCE
    void updatePreferences({
      theme: nextPreference(resolveTheme(held, systemPrefersDark()))
    })
  }, [])

  const value = useMemo(
    () => ({ theme, preference, toggle }),
    [theme, preference, toggle]
  )

  return <ThemeContext value={value}>{children}</ThemeContext>
}
